import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "sonner"
import { FaArrowLeft } from "react-icons/fa"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/modules/shared/ui/components/alert-dialog"
import { RichTextEditor } from "@/modules/shared/ui/components/custom/RichTextEditor"
import {
  useEmailTemplates,
  useUpsertEmailTemplate,
  useDeleteEmailTemplateOverride,
} from "../../hooks/useEmailTemplates"
import { AvailableVariablesPanel } from "../components/AvailableVariablesPanel"
import { EmailPreviewIframe } from "../components/EmailPreviewIframe"

function humanizeType(type: string): string {
  return type.replace(/([A-Z])/g, " $1").trim()
}

// TipTap represents an empty document as "<p></p>" — treat that the same as ""
// so the Save button stays disabled when the editor is visually empty.
function normalizeHtml(html: string): string {
  return !html || html === "<p></p>" ? "" : html
}

export default function EmailTemplateEditorPage() {
  const { type } = useParams<{ type: string }>()
  const navigate = useNavigate()
  const organizationId = useSelector(selectCurrentOrganizationId)

  const { data: templates = [] } = useEmailTemplates(
    organizationId ?? undefined,
  )
  const template = templates.find((t) => t.type === type)

  // Always show the effective body in the editor:
  // - custom body if the org has one saved
  // - system default body otherwise (so the editor isn't blank)
  const effectiveBody = template?.customBody ?? template?.defaultBody ?? ""
  const [body, setBody] = useState(effectiveBody)

  // Sync when the template loads (React Query fetch completes) or after a reset.
  useEffect(() => {
    if (template) setBody(template.customBody ?? template.defaultBody ?? "")
  }, [template])

  // Pristine means "nothing worth saving yet":
  // - if a custom body exists: unchanged when body still matches it
  // - if no custom body: unchanged when body still matches the system default
  const baselineBody = template?.customBody ?? template?.defaultBody ?? ""
  const isPristine = normalizeHtml(body) === normalizeHtml(baselineBody)

  const upsert = useUpsertEmailTemplate(organizationId!)
  const deleteOverride = useDeleteEmailTemplateOverride(organizationId!)

  const handleSave = async () => {
    if (!type) return
    try {
      await upsert.mutateAsync({ type, body })
      toast.success("Email template saved.")
    } catch {
      toast.error("Failed to save template.")
    }
  }

  const handleReset = async () => {
    if (!type) return
    try {
      await deleteOverride.mutateAsync(type)
      toast.success("Template reset to default.")
    } catch {
      toast.error("Failed to reset template.")
    }
  }

  if (!template) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
        Template not found.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Back to email templates"
            onClick={() => navigate("/settings/email-templates")}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <FaArrowLeft />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              {humanizeType(type!)}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Edit the email body for this template type.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {template.customBody && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={deleteOverride.isPending}>
                  Reset to Default
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Reset to default?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove your custom body and revert to the system
                    default. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset}>
                    Reset
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <Button
            onClick={handleSave}
            disabled={isPristine || upsert.isPending}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {upsert.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Left — editor */}
        <div className="space-y-4">
          <div className="bg-card rounded-xl shadow-sm border border-border p-6 space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                Subject
              </label>
              <Input
                value={template.subject}
                readOnly
                className="bg-muted text-muted-foreground cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground">
                Subject lines are managed by the system and cannot be edited.
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">Body</label>
              <RichTextEditor content={body} onChange={setBody} />
            </div>
          </div>

          <AvailableVariablesPanel sampleData={template.sampleData} />
        </div>

        {/* Right — preview */}
        <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Preview</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Updates as you type. Uses sample data for placeholders.
            </p>
          </div>
          <EmailPreviewIframe
            orgId={organizationId!}
            type={type!}
            body={body}
          />
        </div>
      </div>
    </div>
  )
}
